import React, { useState, useCallback } from 'react'
import {
  Box,
  Typography,
  Chip,
  IconButton,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Snackbar,
  Alert,
  Tooltip,
} from '@mui/material'
import { Star, StarBorder, Share, Delete, Launch } from '@mui/icons-material'
import { styled } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import apiService from '../services/ApiService'
const convertArrayToDate = (dateArray) => {
  if (!dateArray || !Array.isArray(dateArray)) return null
  // Array format: [year, month, day, hour, minute, second, nanosecond]
  // Note: Month is 1-based in the array but Date constructor expects 0-based
  const [year, month, day, hour, minute, second, nanosecond] = dateArray
  return new Date(
    year,
    month - 1,
    day,
    hour,
    minute,
    second,
    Math.floor(nanosecond / 1000000),
  )
}
const BookmarkCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  border: '1px solid #e2e8f0',
  borderRadius: '8px',
  transition: 'all 0.2s ease',
  cursor: 'pointer',
  '&:hover': {
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
    transform: 'translateY(-1px)',
  },
}))

const ActionButton = styled(IconButton)(({ theme }) => ({
  padding: '8px',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'scale(1.1)',
  },
}))

const StarButton = styled(IconButton)(({ theme, starred }) => ({
  padding: '8px',
  color: starred ? '#ffda1b' : '#6b7280',
  transition: 'all 0.2s ease',
  '&:hover': {
    transform: 'scale(1.1)',
    color: starred ? '#f59e0b' : '#ffda1b',
  },
}))

const EnhancedBookmarkCard = ({
  bookmark,
  onDelete,
  onUpdate,
  showSnackbar,
  readOnly = false,
}) => {
  const navigate = useNavigate()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [isStarring, setIsStarring] = useState(false)
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  })

  const getCategoryColor = (category) => {
    switch (category) {
      case 'Cabinet Decision':
        return '#4e342e'
      case 'Ministerial Decision':
        return '#1976d2'
      case 'Federal Tax Authority Decision':
        return '#2e7d32'
      case 'Administrative Decision':
        return '#4e342e'
      case 'ZATCA Decision':
        return '#d81b60'
      case 'Federal Tax Authority Guide':
        return '#3f51b5'
      case 'Federal Tax Authority Service Guide':
        return '#d81b60'
      case 'Public Clarification':
        return '#f44336'
      case 'Zakat, Tax and Customs Authority':
        return '#2e7d32'
      case 'Foreign Account Tax Compliance Act Guide':
        return '#673ab7'
      case 'User Manual':
        return '#ff9800'
      case 'VAT Taxpayer Guide':
        return '#0097a7'
      case 'Circular':
        return '#1976d2'
      case 'Tax Bulletin and Circular':
        return '#d81b60'
      case 'Oman Tax Authority':
        return '#37474f'
      case 'Qatar Tax Authority':
        return '#37474f'
      case 'National Bureau for Revenue':
        return '#37474f'
      case 'Bylaws':
        return '#0097a7'
      case 'Executive Rules and Instructions':
        return '#673ab7'
      case 'Executive Regulations':
        return '#673ab7'
      case 'Implementing Regulations':
        return '#673ab7'
      case 'Executive Bylaws':
        return '#673ab7'
      case 'Judicial Decision':
        return '#673ab7'
      case 'Foreign Account Tax Compliance Act FAQ and Flowchart':
        return '#3f51b5'
      default:
        return '#6b7280'
    }
  }

  // Handle card click to navigate to article
  const handleCardClick = useCallback(
    (event) => {
      // Prevent navigation if clicking on action buttons
      if (event.target.closest('[data-action-button]')) {
        return
      }

      if (bookmark.url) {
        // If it's an external URL, open in new tab
        if (bookmark.url.startsWith('http')) {
          window.open(bookmark.url, '_blank', 'noopener,noreferrer')
        } else {
          // Internal navigation
          navigate(bookmark.url)
        }
      }
    },
    [bookmark.url, navigate],
  )

  // Handle star toggle
  const handleStarToggle = useCallback(
    async (event) => {
      event.stopPropagation()
      if (readOnly) return

      if (isStarring) return

      setIsStarring(true)
      try {
        const response = await apiService.put(
          `/api/v1/bookmarks/${bookmark.id}/star`,
        )

        // Update the bookmark in parent component
        if (onUpdate) {
          onUpdate(response)
        }

        const message = response.isStarred
          ? 'Bookmark starred! ⭐'
          : 'Bookmark unstarred'

        if (showSnackbar) {
          showSnackbar(message, 'success')
        } else {
          setSnackbar({
            open: true,
            message,
            severity: 'success',
          })
        }
      } catch (error) {
        console.error('Error toggling bookmark star:', error)
        const errorMessage = 'Failed to update bookmark star'

        if (showSnackbar) {
          showSnackbar(errorMessage, 'error')
        } else {
          setSnackbar({
            open: true,
            message: errorMessage,
            severity: 'error',
          })
        }
      } finally {
        setIsStarring(false)
      }
    },
    [bookmark.id, isStarring, onUpdate, readOnly, showSnackbar],
  )

  // Handle share functionality
  const handleShare = useCallback(
    async (event) => {
      event.stopPropagation()

      const url = bookmark.url.startsWith('http')
        ? bookmark.url
        : `${window.location.origin}${bookmark.url}`

      const title = bookmark.title || 'Bookmarked Article'
      const cleanTitle = title.replace(/<[^>]*>/g, '')

      const shareData = {
        title: cleanTitle,
        text: `Check out this article: ${cleanTitle}`,
        url: url,
      }

      try {
        let shareMethod = 'link'

        // Check if Web Share API is supported (mainly mobile)
        if (navigator.share) {
          shareMethod = 'native'
          await navigator.share(shareData)

          if (showSnackbar) {
            showSnackbar('Article shared successfully! 📤', 'success')
          } else {
            setSnackbar({
              open: true,
              message: 'Article shared successfully! 📤',
              severity: 'success',
            })
          }
        } else {
          // Fallback to clipboard copy
          await navigator.clipboard.writeText(url)

          if (showSnackbar) {
            showSnackbar('Article link copied to clipboard! 📋', 'success')
          } else {
            setSnackbar({
              open: true,
              message: 'Article link copied to clipboard! 📋',
              severity: 'success',
            })
          }
        }
      } catch (error) {
        if (error.name !== 'AbortError') {
          // Fallback to clipboard if share fails
          try {
            await navigator.clipboard.writeText(url)

            if (showSnackbar) {
              showSnackbar('Article link copied to clipboard! 📋', 'success')
            } else {
              setSnackbar({
                open: true,
                message: 'Article link copied to clipboard! 📋',
                severity: 'success',
              })
            }
          } catch (clipboardError) {
            const errorMessage = 'Failed to share article'

            if (showSnackbar) {
              showSnackbar(errorMessage, 'error')
            } else {
              setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error',
              })
            }
          }
        }
      }
    },
    [bookmark.url, bookmark.title, showSnackbar],
  )

  // Handle delete confirmation
  const handleDeleteClick = useCallback((event) => {
    event.stopPropagation()
    if (readOnly) return
    setDeleteDialogOpen(true)
  }, [readOnly])

  const handleDeleteConfirm = useCallback(async () => {
    try {
      await apiService.delete(`/api/v1/bookmarks/${bookmark.id}`)

      if (onDelete) {
        onDelete(bookmark.id)
      }

      if (showSnackbar) {
        showSnackbar('Bookmark deleted successfully', 'success')
      } else {
        setSnackbar({
          open: true,
          message: 'Bookmark deleted successfully',
          severity: 'success',
        })
      }
    } catch (error) {
      console.error('Error deleting bookmark:', error)
      const errorMessage = 'Failed to delete bookmark'

      if (showSnackbar) {
        showSnackbar(errorMessage, 'error')
      } else {
        setSnackbar({
          open: true,
          message: errorMessage,
          severity: 'error',
        })
      }
    } finally {
      setDeleteDialogOpen(false)
    }
  }, [bookmark.id, onDelete, showSnackbar])

  const handleDeleteCancel = useCallback(() => {
    setDeleteDialogOpen(false)
  }, [])

  const handleSnackbarClose = useCallback(() => {
    setSnackbar((prev) => ({ ...prev, open: false }))
  }, [])

  return (
    <>
      <BookmarkCard onClick={handleCardClick}>
        <CardContent>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              // mb: 2,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {bookmark.articleSlug && (
                <Chip
                  label={bookmark.articleSlug}
                  size='small'
                  sx={{
                    bgcolor: 'white',
                    color: getCategoryColor(bookmark.category),
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    height: '24px',
                    border: `1.5px solid ${getCategoryColor(
                      bookmark.category,
                    )}`,
                    mb: 1,
                  }}
                />
              )}
              <Typography
                variant='h6'
                sx={{
                  fontWeight: 600,
                  mb: 1,
                  color: '#232536',
                  cursor: 'pointer',
                  '&:hover': {
                    color: '#ffda1b',
                  },
                }}
              >
                {bookmark.title}
                <Launch
                  sx={{
                    ml: 1,
                    fontSize: '1rem',
                    opacity: 0.7,
                    transition: 'opacity 0.2s ease',
                  }}
                />
              </Typography>
              <Typography
                variant='body2'
                color='text.secondary'
                sx={{
                  mb: 2,
                  lineHeight: 1.5,
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {bookmark.description || 'No description available'}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  mb: 2,
                }}
              >
                {bookmark.category && (
                  <Chip
                    label={bookmark.category}
                    size='small'
                    sx={{
                      bgcolor: getCategoryColor(bookmark.category),
                      color: 'white',
                      fontWeight: 500,
                    }}
                  />
                )}
              </Box>

              {bookmark.tags && bookmark.tags.length > 0 && (
                <Box
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 0.5,
                    mb: 2,
                  }}
                >
                  {bookmark.tags.map((tag, index) => (
                    <Chip
                      key={index}
                      label={tag}
                      size='small'
                      variant='outlined'
                      sx={{ fontSize: '0.75rem' }}
                    />
                  ))}
                </Box>
              )}

              <Typography variant='caption' color='text.secondary'>
                Saved on{' '}
                {bookmark.createdAt
                  ? convertArrayToDate(bookmark.createdAt).toLocaleDateString()
                  : 'N/A'}
              </Typography>
            </Box>

            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 1,
                ml: 2,
              }}
            >
              {!readOnly && (
                <Tooltip
                  title={
                    bookmark.isStarred
                      ? 'Remove from favorites'
                      : 'Add to favorites'
                  }
                >
                  <StarButton
                    data-action-button
                    starred={bookmark.isStarred}
                    onClick={handleStarToggle}
                    disabled={isStarring}
                  >
                    {bookmark.isStarred ? <Star /> : <StarBorder />}
                  </StarButton>
                </Tooltip>
              )}

              <Tooltip title='Share article'>
                <ActionButton
                  data-action-button
                  sx={{ color: '#232536' }}
                  onClick={handleShare}
                >
                  <Share />
                </ActionButton>
              </Tooltip>

              {!readOnly && (
                <Tooltip title='Delete bookmark'>
                  <ActionButton
                    data-action-button
                    sx={{ color: '#ef4444' }}
                    onClick={handleDeleteClick}
                  >
                    <Delete />
                  </ActionButton>
                </Tooltip>
              )}
            </Box>
          </Box>
        </CardContent>
      </BookmarkCard>

      {/* Delete Confirmation Dialog */}
      {!readOnly && (
        <Dialog
          open={deleteDialogOpen}
          onClose={handleDeleteCancel}
          aria-labelledby='delete-dialog-title'
          aria-describedby='delete-dialog-description'
        >
          <DialogTitle id='delete-dialog-title'>Delete Bookmark</DialogTitle>
          <DialogContent>
            <DialogContentText id='delete-dialog-description'>
              Are you sure you want to delete this bookmark? This action cannot be
              undone.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={handleDeleteCancel}
              variant='outlined'
              sx={{
                borderColor: '#232536',
                color: '#232536',
                '&:hover': {
                  borderColor: '#3a3e5a',
                  color: '#3a3e5a',
                },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDeleteConfirm}
              color='error'
              variant='contained'
            >
              Delete
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  )
}

export default EnhancedBookmarkCard
